// 한국형 배송비 정책 타입/파서 (2026-08-24 KST 추가)
// 대시보드 "기본 배송 설정"(shop metadata)과 상품별 "배송 설정"(product metadata)이
// 공유하는 JSON 스키마. Saleor 백엔드(saleor/shipping_policy/policy.py)와
// 스토어프론트가 같은 키/스키마를 읽는다.

export const SHIPPING_SETTINGS_METADATA_KEY = "farmstore.shipping_settings";
export const PRODUCT_SHIPPING_METADATA_KEY = "farmstore.product_shipping";

export type ShippingPayment = "prepaid" | "cod" | "buyer_choice";

export type ShippingFeeType =
  | "free"
  | "conditional_free"
  | "fixed"
  | "per_quantity"
  | "per_amount"
  | "repeat_quantity";

// region: 도서/산간 지역 배송비 화면의 우편번호별 요금표를 쓴다 (2026-08-24 KST 추가)
export type SurchargeMode = "zone" | "region" | "none";

/** 수량별/금액별 구간 한 행 — 입력 편의를 위해 폼 상태는 문자열로 유지 */
export interface ShippingBracketForm {
  limit: string;
  fee: string;
}

/** 배송비 정책 폼 상태 (공통 설정과 상품별 개별 설정이 공유) */
export interface ShippingPolicyFormData {
  feeType: ShippingFeeType;
  baseFee: string;
  freeThreshold: string;
  brackets: ShippingBracketForm[];
  repeatUnit: string;
  surchargeMode: SurchargeMode;
  jejuFee: string;
  islandFee: string;
}

/** 기본 배송 설정 = 정책 + 배송비 결제 방식 */
export interface ShippingSettingsFormData extends ShippingPolicyFormData {
  payment: ShippingPayment;
}

export const DEFAULT_SHIPPING_POLICY_FORM: ShippingPolicyFormData = {
  feeType: "free",
  baseFee: "",
  freeThreshold: "",
  brackets: [{ limit: "", fee: "" }],
  repeatUnit: "",
  surchargeMode: "none",
  jejuFee: "",
  islandFee: "",
};

export const DEFAULT_SHIPPING_SETTINGS_FORM: ShippingSettingsFormData = {
  payment: "prepaid",
  ...DEFAULT_SHIPPING_POLICY_FORM,
};

const FEE_TYPES: ShippingFeeType[] = [
  "free",
  "conditional_free",
  "fixed",
  "per_quantity",
  "per_amount",
  "repeat_quantity",
];

const toFormNumber = (value: unknown): string =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 ? String(value) : "";

const toSaveNumber = (value: string): number => {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

/** 저장된 JSON(object)을 정책 폼 상태로 변환 */
export function parseShippingPolicyForm(parsed: Record<string, unknown>): ShippingPolicyFormData {
  const feeType = FEE_TYPES.includes(parsed.feeType as ShippingFeeType)
    ? (parsed.feeType as ShippingFeeType)
    : "free";

  const rawBrackets = Array.isArray(parsed.brackets) ? parsed.brackets : [];
  const brackets: ShippingBracketForm[] = rawBrackets
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(row => ({ limit: toFormNumber(row.limit), fee: toFormNumber(row.fee) }));

  const surcharge =
    typeof parsed.surcharge === "object" && parsed.surcharge !== null
      ? (parsed.surcharge as Record<string, unknown>)
      : {};

  return {
    feeType,
    baseFee: toFormNumber(parsed.baseFee),
    freeThreshold: toFormNumber(parsed.freeThreshold),
    brackets: brackets.length > 0 ? brackets : [{ limit: "", fee: "" }],
    repeatUnit: toFormNumber(parsed.repeatUnit),
    surchargeMode:
      surcharge.mode === "zone" || surcharge.mode === "region" ? surcharge.mode : "none",
    jejuFee: toFormNumber(surcharge.jejuFee),
    islandFee: toFormNumber(surcharge.islandFee),
  };
}

/** shop metadata 값(JSON 문자열) → 기본 배송 설정 폼 상태 */
export function parseShippingSettings(value: string | undefined): ShippingSettingsFormData {
  if (!value) {
    return DEFAULT_SHIPPING_SETTINGS_FORM;
  }

  try {
    const parsed = JSON.parse(value);

    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_SHIPPING_SETTINGS_FORM;
    }

    const payment: ShippingPayment = ["prepaid", "cod", "buyer_choice"].includes(parsed.payment)
      ? parsed.payment
      : "prepaid";

    return { payment, ...parseShippingPolicyForm(parsed) };
  } catch {
    return DEFAULT_SHIPPING_SETTINGS_FORM;
  }
}

/** 정책 폼 상태 → 저장용 JSON object (선택한 유형에 필요한 필드만 기록) */
export function serializeShippingPolicy(form: ShippingPolicyFormData): Record<string, unknown> {
  const data: Record<string, unknown> = { feeType: form.feeType };

  if (["conditional_free", "fixed", "repeat_quantity"].includes(form.feeType)) {
    data.baseFee = toSaveNumber(form.baseFee);
  }

  if (form.feeType === "conditional_free") {
    data.freeThreshold = toSaveNumber(form.freeThreshold);
  }

  if (form.feeType === "per_quantity" || form.feeType === "per_amount") {
    data.brackets = form.brackets
      .map(row => ({ limit: toSaveNumber(row.limit), fee: toSaveNumber(row.fee) }))
      .filter(row => row.limit > 0);
  }

  if (form.feeType === "repeat_quantity") {
    data.repeatUnit = Math.max(1, Math.floor(toSaveNumber(form.repeatUnit)));
  }

  if (form.surchargeMode === "zone") {
    data.surcharge = {
      mode: "zone",
      jejuFee: toSaveNumber(form.jejuFee),
      islandFee: toSaveNumber(form.islandFee),
    };
  } else if (form.surchargeMode === "region") {
    // 금액은 우편번호별 요금표에 있으므로 모드만 저장한다
    data.surcharge = { mode: "region" };
  } else {
    data.surcharge = { mode: "none" };
  }

  return data;
}

/** 저장 전 정책 검증 — 문제가 있으면 에러 메시지, 없으면 null */
export function validateShippingPolicy(form: ShippingPolicyFormData): string | null {
  if (form.feeType === "conditional_free") {
    if (toSaveNumber(form.baseFee) <= 0) {
      return "조건부 무료배송의 기본 배송비를 입력해주세요";
    }

    if (toSaveNumber(form.freeThreshold) <= 0) {
      return "무료배송 기준 금액을 입력해주세요";
    }
  }

  if (form.feeType === "fixed" && toSaveNumber(form.baseFee) <= 0) {
    return "고정 배송비 금액을 입력해주세요";
  }

  if (
    (form.feeType === "per_quantity" || form.feeType === "per_amount") &&
    !form.brackets.some(row => toSaveNumber(row.limit) > 0)
  ) {
    return "배송비 조건을 1개 이상 입력해주세요";
  }

  if (form.feeType === "repeat_quantity") {
    if (toSaveNumber(form.baseFee) <= 0) {
      return "반복 수량 배송비의 기본 배송비를 입력해주세요";
    }

    if (toSaveNumber(form.repeatUnit) <= 0) {
      return "반복 부과할 상품 수량을 입력해주세요";
    }
  }

  return null;
}
